import mitt from 'mitt'
import { DateFilterState } from 'features/dashboards/components/Filters/types'

enum StorageKeys {
  PendingFeedback = 'pending-feedback',
  DashboardFilters = 'dashboard-filters'
}

const DASHBOARD_FILTERS_VERSION = 2 as const

type StoredDashboardFiltersV2 = {
  version: typeof DASHBOARD_FILTERS_VERSION
  byWorkspace: Record<string, DashboardFiltersStorage>
}

function isStoredDashboardFiltersV2(parsed: unknown): parsed is StoredDashboardFiltersV2 {
  if (!parsed || typeof parsed !== 'object') return false
  const o = parsed as { version?: unknown; byWorkspace?: unknown }
  if (o.version !== DASHBOARD_FILTERS_VERSION) return false
  if (o.byWorkspace === null || typeof o.byWorkspace !== 'object' || Array.isArray(o.byWorkspace)) return false
  return true
}

/** Drops the key if JSON is corrupt or not `{ version: 2, byWorkspace: { ... } }`. */
function readDashboardFiltersStore(): StoredDashboardFiltersV2 {
  const rawData = localStorage.getItem(StorageKeys.DashboardFilters)
  if (!rawData) {
    return { version: DASHBOARD_FILTERS_VERSION, byWorkspace: {} }
  }
  try {
    const parsed: unknown = JSON.parse(rawData)
    if (isStoredDashboardFiltersV2(parsed)) {
      return parsed
    }
  } catch {
    // invalid JSON
  }
  localStorage.removeItem(StorageKeys.DashboardFilters)
  return { version: DASHBOARD_FILTERS_VERSION, byWorkspace: {} }
}

function writeDashboardFiltersStore(store: StoredDashboardFiltersV2): void {
  if (Object.keys(store.byWorkspace).length === 0) {
    localStorage.removeItem(StorageKeys.DashboardFilters)
    return
  }
  localStorage.setItem(StorageKeys.DashboardFilters, JSON.stringify(store))
}

function isValidDashboardWorkspaceId(workspaceId: number): boolean {
  return Number.isFinite(workspaceId) && workspaceId > 0
}

type Events = {
  feedbackChange: boolean
}

type DashboardFiltersStorage = {
  academicYear?: DateFilterState | null
  curriculum?: string[] | null
  courseTitle?: string[] | null
  courseLevel?: string[] | null
  campus?: string[] | null
  spaceId?: string[] | null
  tutorUserId?: string[] | null
}

const emitter = mitt<Events>()

class LocalStorageService {
  public pendingFeedback: boolean | undefined

  constructor() {
    this.pendingFeedback = this.getPendingFeedback()
  }

  setPendingFeedback(value: boolean) {
    this.pendingFeedback = value
    const stringValue = value ? '1' : '0'
    localStorage.setItem(StorageKeys.PendingFeedback, stringValue)
    emitter.emit('feedbackChange', value)
  }

  getPendingFeedback(): boolean | undefined {
    const stringValue = localStorage.getItem(StorageKeys.PendingFeedback)
    return stringValue ? stringValue === '1' : undefined
  }

  onFeedbackChange(callback: (value: boolean) => void) {
    emitter.on('feedbackChange', callback)
  }

  offFeedbackChange(callback: (value: boolean) => void) {
    emitter.off('feedbackChange', callback)
  }

  setDashboardFilters(workspaceId: number, filters: DashboardFiltersStorage) {
    if (!isValidDashboardWorkspaceId(workspaceId)) return
    const store = readDashboardFiltersStore()
    store.byWorkspace[String(workspaceId)] = filters
    writeDashboardFiltersStore(store)
  }

  getDashboardFilters(workspaceId: number): DashboardFiltersStorage | null {
    if (!isValidDashboardWorkspaceId(workspaceId)) return null

    const store = readDashboardFiltersStore()
    return store.byWorkspace[String(workspaceId)] ?? null
  }

  clearDashboardFilters(workspaceId: number): void {
    if (!isValidDashboardWorkspaceId(workspaceId)) return
    const store = readDashboardFiltersStore()
    delete store.byWorkspace[String(workspaceId)]
    writeDashboardFiltersStore(store)
  }
}

export const localStorageService = new LocalStorageService()
